import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';

import {Chart} from 'angular-highcharts';
import {MockDataService} from '../../../services/mock-data.service';

import {UserService} from '../../../services/user.service';
import * as moment from 'moment';
import {months} from 'moment';
import {getWeek, MONTHS} from '../../../lib/DateUtil';
import {ElectricityService} from '../../../services/electricity.service';
import {HappyHourService} from '../../../services/happy-hour.service';
import {ApplicationService} from '../../../services/application.service';
import {BasePopover} from '../../../classes/BasePopover';
import {PopoverRef} from '../../../popovers/popover/popover-ref';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {Observable, of, throwError} from 'rxjs';
import {TrackAnalyticsService} from '../../../services/track-analytics.service';
import {Tariff} from '../../../classes/user';
import {ProfileService} from '../../../services/profile.service';

@Component({
    selector: 'app-comparison-details',
    templateUrl: './comparison-details.component.html',
    styleUrls: ['comparison-details.component.scss'],
    viewProviders: []
})

export class ComparisonDetailsComponent extends BasePopover implements OnInit, AfterViewInit {

    MONTHS = MONTHS;

    year = 0;

    mode = 1;
    timeframe = 3;
    position = 1;

    compare: any = {
        left_day: 0, left_kw: 0, left_month: 0, left_year: 0,
        right_day: 0, right_kw: 0, right_month: 0, right_year: 0
    };

    limit = 6;
    disabled = true;
    chart: Chart = null;

    lastYearColor = '#f1ef7f';
    thisYearColor = '#e3e000';

    infoVisible = false;

    seriesLegend = [];
    displayMode: 'consumption' | 'cost' | 'feedin' = 'consumption';
    tariffs: Tariff[] = null;
    tariffsAvailable = false;

    @ViewChild('timeframeChangeDropdown', {static: true}) timeframeChangeDropdown: ElementRef;
    private timeframeChangeObserver: MutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
                const value = mutation.target['value'];
                if (this.timeframe !== value) {
                    this.setTimeframe(value);
                }
            }
        });
    });

    @ViewChild('displayModeChangeDropdown', {static: true}) displayModeChangeDropdown: ElementRef;
    private displayModeChangeObserver: MutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
                const value = mutation.target['value'];
                this.setValueMode(value);
            }
        });
    });

    constructor(protected popoverRef: PopoverRef,
                public userService: UserService,
                public mockDataService: MockDataService,
                public application: ApplicationService,
                private electricityService: ElectricityService,
                private analytics: TrackAnalyticsService,
                private happyHour: HappyHourService,
                private profile: ProfileService) {
        super(popoverRef);
    }

    ngOnInit() {
        this.constructComparisonDates();
        if (this.timeframeChangeDropdown) {
            this.timeframeChangeObserver.observe(this.timeframeChangeDropdown.nativeElement, {attributeFilter: ['value']});
        }
        if (this.displayModeChangeObserver) {
            this.displayModeChangeObserver.observe(this.displayModeChangeDropdown.nativeElement, {attributeFilter: ['value']});
        }
        this.initializeTariffs();
    }

    ngAfterViewInit(): void {
        this.initializeStaticChart();
    }

    setValueMode(mode: 'consumption' | 'cost' | 'feedin') {
        if (this.displayMode === mode) {
            return;
        }
        this.displayMode = mode;
        this.resetChart();
        switch (this.displayMode) {
            case 'consumption':
                break;
            case 'cost':
                break;
            case 'feedin':
                break;
            default:
                console.log('default triggered for some weird reason');
        }
    }


    setMode(mode: number) {
        if (mode !== this.mode) {
            this.mode = mode;

            this.resetChart();

            this.analytics.trackEvent({
                action: 'screen_view',
                properties: {
                    category: 'Screens',
                    label: 'screen: Vergleich - ' + (this.mode === 2 ? 'Anpassbar' : 'Statisch') +
                        '; previous_screen: Vergleich - ' + (this.mode === 2 ? 'Statisch' : 'Anpassbar')
                }
            });
        }
    }

    setTimeframe(timeframe: string) {
        if ((parseInt(timeframe, 10) !== this.timeframe) && (!this.disabled)) {
            this.timeframe = parseInt(timeframe, 10);
            this.position = 1;
            this.constructComparisonDates();
            this.resetChart();
        }
    }

    setCompare(key, value) {
        this.compare[key] = parseInt(value, 10);
        this.resetChart();
    }

    positionForward() {
        if ((this.position > 1) && (!this.disabled)) {
            this.position--;
            this.resetChart();
        }
    }

    positionBack() {
        if (!this.disabled) {
            this.position++;
            this.resetChart();
        }
    }

    positionNow() {
        if (!this.disabled) {
            this.position = 1;
            this.resetChart();
        }
    }

    resetChart() {
        if (this.chart.ref) {
            this.chart.ref.showLoading();
        }

        this.disabled = true;
        if (this.mode === 2) {
            this.initializeDynamicChart();
        } else {
            this.initializeStaticChart();
        }
    }

    loop(start: number, times: number) {
        const loop = [];
        for (let i = start; i < (start + times); i++) {
            loop.push(i);
        }
        return loop;
    }

    determineCurrentBasePrice(): string {
        if (!this.tariffs) {
            return;
        }
        let unit = '€ / ';
        let valueFormatted = this.tariffs[0].basePrice;
        switch (this.timeframe) {
            case 1:
                unit += 'Tag';
                valueFormatted = valueFormatted / 365;
                break;
            case 2:
                unit += 'Woche';
                valueFormatted = valueFormatted / 52.1429;
                break;
            case 3:
                unit += 'Monat';
                valueFormatted = valueFormatted / 12;
                break;
            case 4:
                unit += 'Jahr';
                break;
        }
        const valueFormattedStr = valueFormatted.toLocaleString('de-DE', {
            maximumFractionDigits: 2
        });
        return `${valueFormattedStr} ${unit}`;
    }


    private constructComparisonDates() {
        const dateRight = new Date();
        const dateLeft = new Date();

        this.year = dateRight.getFullYear();

        switch (this.timeframe) {
            case 2: { // Woche
                dateLeft.setDate(dateRight.getDate() - 7);
                break;
            }
            case 3: { // Monat
                dateLeft.setMonth(dateRight.getMonth() - 1);
                break;
            }
            case 4: { // Jahr
                dateLeft.setFullYear(dateRight.getFullYear() - 1);
                break;
            }
            default: { // Tag
                dateLeft.setDate(dateRight.getDate() - 1);
            }
        }

        this.compare = {
            left_day: dateLeft.getDate(),
            left_kw: getWeek(dateLeft),
            left_month: dateLeft.getMonth() + 1,
            left_year: dateLeft.getFullYear(),
            right_day: dateRight.getDate(),
            right_kw: getWeek(dateRight),
            right_month: dateRight.getMonth() + 1,
            right_year: dateRight.getFullYear()
        };
    }


    private getStaticDiagramData() {
        const years = [
            {year: this.year - 1, color: this.lastYearColor},
            {year: this.year, color: this.thisYearColor}
        ];

        this.chart.ref.showLoading('Auswertung wird geladen…');

        for (const year of years) {
            const s = of(this.displayMode).pipe(
                mergeMap(mode => {
                    const dates = this.determineStaticDatesByTimeframe(year);
                    switch (mode) {
                        case 'consumption':
                        case 'cost':
                            return this.electricityService.getConsumptionForTimeframe(
                                dates.from, dates.to, dates.timeframe);
                        case 'feedin':
                            return this.electricityService.getFeedinForTimeframe(
                                dates.from, dates.to, dates.timeframe);
                    }
                }),
                mergeMap((res) => {
                    if (!res) {
                        return throwError(null);
                    }
                    if (res.length === 0) {
                        return throwError(null);
                    }
                    return of(res);
                }),
                map((res: any) => {
                    let categories = [];
                    const series = {name: null, data: [], color: '#ffffff', originalData: res};

                    switch (this.timeframe) {
                        case 2: {
                            const consumptions = [];
                            for (const con of res) {
                                const kw = getWeek(new Date(con.timestamp));
                                const index = consumptions.map((el) => el.kw).indexOf(kw);
                                const value = this.determineSeriesValue(con);

                                if (index >= 0) {
                                    consumptions[index].wh += value;
                                } else {
                                    consumptions.push({
                                        kw,
                                        ts: con.timestamp,
                                        wh: value
                                    });
                                }
                            }
                            for (const c of consumptions) {
                                categories.push('KW ' + c.kw);
                            }

                            const mapped = consumptions.map(
                                (c) => Number(((c.wh) / 1000).toFixed(2))
                            );

                            series.name = new Date(res[res.length - 1].timestamp)
                                .getFullYear().toString();
                            series.data = mapped;
                            series.color = year.color;

                            break;
                        }
                        case 4: {
                            let consumption = 0;
                            for (const con of res) {
                                consumption += this.determineSeriesValue(con);
                            }

                            const mapped = [Number(((consumption) / 1000).toFixed(2))];
                            categories = ['', ''];

                            series.name = year.year;
                            series.data = mapped;
                            series.color = year.color;
                            break;
                        }
                        default: {
                            for (const con of res) {
                                const date = new Date(con.timestamp);
                                switch (this.timeframe) {
                                    case 3: {
                                        categories.push(this.MONTHS[date.getMonth()]);
                                        break;
                                    }
                                    default: {
                                        categories.push(date.getDate() + '. ' +
                                            this.MONTHS[date.getMonth()]);
                                    }
                                }
                            }

                            const mapped = res.map((consumption) => {
                                    const value = this.determineSeriesValue(consumption);
                                    return Number(((value / 1000)).toFixed(2));
                                }
                            );

                            series.name = new Date(res[res.length - 1].timestamp)
                                .getFullYear().toString();
                            series.data = mapped;
                            series.color = year.color;
                        }

                    }

                    this.disabled = false;
                    return {categories, series};
                }),
                catchError((error) => {
                    if (this.chart) {
                        this.chart.ref.showLoading(
                            'Keine Auswertung für diesen Zeitraum verfügbar!');
                        this.disabled = false;
                    }
                    return of(false);
                })
            ).subscribe(
                (result: any) => {
                    if (!result) {
                        return;
                    }
                    this.chart.addSeries(result.series, true, true);
                    this.chart.ref.update({xAxis: {categories: result.categories}});
                    this.chart.ref.hideLoading();

                    s.unsubscribe();
                },
            );

        }

    }

    private getMockStatic(): void {
        this.chart.ref.showLoading('Auswertung wird geladen…');
        for (let year of [{year: this.year - 1, color: this.lastYearColor}, {
            year: this.year,
            color: this.thisYearColor
        }]) {

            const req_params = this.determineStaticDatesByTimeframe(year);
            const from = req_params.from;
            const to = req_params.to;
            const timeframe = req_params.timeframe;

            const s = this.mockDataService.getConsumptionByDate(timeframe, from, to).subscribe(
                (data: any) => {
                    if (!('data' in data)) {
                        return;
                    }
                    if (data.data.length === 0) {
                        this.chart.ref.hideLoading();
                        this.disabled = false;
                    }
                    let categories: any = [];

                    switch (this.timeframe) {
                        case 2: {
                            const consumption = [];
                            for (const con of data.data) {
                                const kw = getWeek(new Date(con.timestamp));
                                const index = consumption.map(c => c.kw).indexOf(kw);
                                if (index > -1) {
                                    if ('measured' in con) {
                                        consumption[index].wh += con.measured;
                                    }
                                } else {
                                    const wh = 'measured' in con ? con.measured : 0;
                                    consumption.push({kw, wh});
                                }
                            }
                            for (const con of consumption) {
                                categories.push('KW ' + con.kw);
                            }

                            const series: any = {
                                name: new Date(data.data[data.data.length - 1].timestamp).getFullYear().toString(),
                                data:
                                    consumption.map((con: any) => {
                                        return Number((con.wh / 1000).toFixed(2));
                                    }),
                                color: year.color
                            };
                            this.chart.addSeries(series, true, true);
                            break;
                        }
                        case 4: {
                            let consumption = 0;
                            for (const con of data.data) {
                                if ('measured' in con) {
                                    consumption += con.measured;
                                }
                            }
                            categories = ['', ''];
                            const series: any = {
                                name: year.year,
                                data: [Number((consumption / 1000).toFixed(2))],
                                color: year.color
                            };
                            this.chart.addSeries(series, true, true);
                            break;
                        }
                        default: {
                            for (const con of data.data) {
                                const date = new Date(con.timestamp);
                                switch (this.timeframe) {
                                    case 3: {
                                        categories.push(this.MONTHS[date.getMonth()]);
                                        break;
                                    }
                                    default: {
                                        categories.push(
                                            date.getDate() + '. ' + this.MONTHS[date.getMonth()]);
                                    }
                                }
                            }
                            const series: any = {
                                name: new Date(data.data.last().timestamp).getFullYear().toString(),
                                data:
                                    data.data.map((con: any) => {
                                        return Number((con.measured / 1000).toFixed(2));
                                    }),
                                color: year.color
                            };
                            this.chart.addSeries(series, true, true);
                        }
                    }
                    this.chart.ref.update({xAxis: {categories}});
                    this.chart.ref.hideLoading();
                },
                () => {
                    if (this.chart.ref) {
                        this.chart.ref.showLoading(
                            'Keine Auswertung für diesen Zeitraum verfügbar!');
                    }
                    this.disabled = false;
                }
            );
            this.addSub(s);
        }
    }

    private getDynamicDiagramData() {
        let mode = this.mode;
        let area = this.timeframe;
        let position = this.position;

        let left_from = new Date(0);
        let left_to = new Date(0);

        let right_from = new Date(0);
        let right_to = new Date(0);

        let api = null;

        this.chart.ref.showLoading('Auswertung wird geladen…');

        switch (this.timeframe) {
            case 2: { // Woche
                left_from = new Date(0);
                left_from.setFullYear(this.compare.left_year);
                left_from.setDate(left_from.getDate() + (this.compare.left_kw * 7) - 7);

                left_to = new Date(0);
                left_to.setFullYear(this.compare.left_year);
                left_to.setDate(left_to.getDate() + (this.compare.left_kw * 7) - 1);

                right_from = new Date(0);
                right_from.setFullYear(this.compare.right_year);
                right_from.setDate(right_from.getDate() + (this.compare.right_kw * 7) - 7);

                right_to = new Date(0);
                right_to.setFullYear(this.compare.right_year);
                right_to.setDate(right_to.getDate() + (this.compare.right_kw * 7) - 1);

                api = 'days';

                break;
            }
            case 3: { // Monat
                left_from = new Date(0);
                left_from.setDate(1);
                left_from.setMonth(this.compare.left_month - 1);
                left_from.setFullYear(this.compare.left_year);

                left_to = left_from;

                right_from = new Date(0);
                right_from.setDate(1);
                right_from.setMonth(this.compare.right_month - 1);
                right_from.setFullYear(this.compare.right_year);

                right_to = right_from;

                api = 'months';

                break;
            }
            case 4: { // Jahr
                left_from = new Date(0);
                left_from.setFullYear(this.compare.left_year);

                left_to = new Date(0);
                left_to.setFullYear(this.compare.left_year);
                left_to.setMonth(11);
                left_to.setDate(31);

                right_from = new Date(0);
                right_from.setFullYear(this.compare.right_year);

                right_to = new Date(0);
                right_to.setFullYear(this.compare.right_year);
                right_to.setMonth(11);
                right_to.setDate(31);

                api = 'months';

                break;
            }
            default: { // Tag
                left_from = new Date(0);
                left_from.setFullYear(this.compare.left_year);
                left_from.setMonth(this.compare.left_month - 1);
                left_from.setDate(this.compare.left_day);

                left_to = left_from;

                right_from = new Date(0);
                right_from.setFullYear(this.compare.right_year);
                right_from.setMonth(this.compare.right_month - 1);
                right_from.setDate(this.compare.right_day);

                right_to = right_from;

                api = 'days';
            }
        }

        let left$ = this.electricityService.getConsumptionForTimeframe(left_from, left_to, api)
            .pipe(catchError(error => of(null)));
        let right$ = this.electricityService.getConsumptionForTimeframe(right_from, right_to, api)
            .pipe(catchError(error => of(null)));
        if (this.displayMode === 'feedin') {
            left$ = this.electricityService.getFeedinForTimeframe(left_from, left_to, api)
                .pipe(catchError(error => of(null)));
            right$ = this.electricityService.getFeedinForTimeframe(right_from, right_to, api)
                .pipe(catchError(error => of(null)));
        }

        const sub = forkJoin({left: left$, right: right$}).pipe(
            map((res: any) => {
                const consumptions = {
                    left: {value: 0, timestamp: null},
                    right: {value: 0, timestamp: null}
                };
                for (const key of Object.keys(res)) {
                    if (res[key]) {
                        for (const consumption of res[key]) {
                            const value = this.determineSeriesValue(consumption);
                            consumptions[key].timestamp = consumption.timestamp;
                            consumptions[key].value += value;
                        }
                    }
                }
                return consumptions;
            })
        ).subscribe(
            (results) => {
                if (results) {
                    let left_name = '';
                    let right_name = '';
                    const ldate = moment(results.left.timestamp);
                    const rdate = moment(results.right.timestamp);
                    switch (this.timeframe) {
                        case 1: {
                            left_name = ldate.format('DD.MM.YYYY');
                            right_name = rdate.format('DD.MM.YYYY');
                            break;
                        }
                        case 2: {
                            left_name = `KW ${ldate.week()} ${ldate.year()}`;
                            right_name = `KW ${rdate.week()} ${rdate.year()}`;
                            break;
                        }
                        case 3: {
                            left_name = `${ldate.locale('de-DE').format('MMMM YYYY')}`;
                            right_name = `${rdate.locale('de-DE').format('MMMM YYYY')}`;
                            break;
                        }
                        case 4: {
                            left_name = ldate.year().toString();
                            right_name = rdate.year().toString();
                            break;
                        }
                    }
                    const series: any = {
                        name: null,
                        data: [
                            {
                                name: left_name,
                                y: Number((results.left.value / 1000).toFixed(2)),
                                color: this.lastYearColor
                            },
                            {
                                name: right_name,
                                y: Number((results.right.value / 1000).toFixed(2)),
                                color: this.thisYearColor
                            }
                        ]
                    };
                    this.chart.addSeries(series, true, true);
                    this.chart.ref.hideLoading();
                    this.disabled = false;
                } else {
                    if (this.chart.ref) {
                        this.chart.ref.showLoading(
                            'Keine Auswertung für diesen Zeitraum verfügbar!');
                    }
                    this.disabled = false;
                }
                sub.unsubscribe();
            }
        );
    }

    private getMockDynamic(): void {
        const times = this.determineDynamicComparisonApiParams();
        const left_from = times.left_from;
        const left_to = times.left_to;
        const right_from = times.right_from;
        const right_to = times.right_to;
        const api: string = times.api;

        let left_wh = 0;
        let right_wh = 0;

        this.chart.ref.showLoading('Auswertung wird geladen…');

        const s1 = this.mockDataService.getConsumptionByDate(api, left_from, left_to).subscribe(
            (data: any) => {
                if ('data' in data) {
                    for (const con of data.data) {
                        left_wh += 'measured' in con ? con.measured : 0;
                    }

                    const s2 = this.mockDataService.getConsumptionByDate(
                        api, right_from, right_to
                    ).subscribe((data2: any) => {
                            if ('data' in data2) {
                                for (const con of data2.data) {
                                    if ('measured' in con) {
                                        right_wh += con.measured;
                                    }
                                }

                                let left_name = '';
                                let right_name = '';
                                const ldate = moment(data.data[0].timestamp);
                                const rdate = moment(data2.data[0].timestamp);
                                switch (this.timeframe) {
                                    case 1: {
                                        left_name = ldate.format('DD.MM.YYYY');
                                        right_name = rdate.format('DD.MM.YYYY');
                                        break;
                                    }
                                    case 2: {
                                        left_name = `KW ${ldate.week()} ${ldate.year()}`;
                                        right_name = `KW ${rdate.week()} ${rdate.year()}`;
                                        break;
                                    }
                                    case 3: {
                                        left_name = `${ldate.locale('de-DE')
                                            .format('MMMM YYYY')}`;
                                        right_name = `${rdate.locale('de-DE')
                                            .format('MMMM YYYY')}`;
                                        break;
                                    }
                                    case 4: {
                                        left_name = ldate.year().toString();
                                        right_name = rdate.year().toString();
                                        break;
                                    }
                                }
                                const series: any = {
                                    name: null,
                                    data: [
                                        {
                                            name: left_name,
                                            y: Number((left_wh / 1000).toFixed(2)),
                                            color: this.lastYearColor
                                        },
                                        {
                                            name: right_name,
                                            y: Number((right_wh / 1000).toFixed(2)),
                                            color: this.thisYearColor
                                        },
                                    ]
                                };
                                this.chart.addSeries(series, true, true);
                                this.chart.ref.hideLoading();
                                this.disabled = false;
                            }
                        },
                        () => {
                            if (this.chart.ref) {
                                this.chart.ref.showLoading(
                                    'Keine Auswertung für diesen Zeitraum verfügbar!');
                            }

                            this.disabled = false;
                        }
                    );
                    this.addSub(s2);
                }
            },
            () => {
                if (this.chart.ref) {
                    this.chart.ref.showLoading(
                        'Keine Auswertung für diesen Zeitraum verfügbar!');
                }

                this.disabled = false;
            }
        );
        this.addSub(s1);
    }

    private initializeStaticChart() {
        this.seriesLegend = [];
        const self = this;
        this.chart = new Chart({
            chart: {
                type: 'column',
                backgroundColor: 'rgba(255, 255, 255, 0)',
                marginLeft: 80,
                marginBottom: 30,
                marginTop: 50,
                events: {
                    addSeries(evt) {
                        self.seriesLegend.push({name: evt.options.name, color: evt.options.color});
                    },
                    load(load_evt) {
                        if (self.application.isDemoMode()) {
                            self.getMockStatic();
                            return;
                        }
                        self.getStaticDiagramData();
                    }
                }
            },
            title: {
                text: null
            },
            xAxis: {
                // type: 'categories',
                categories: [],
                labels: {
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '18px'
                    }
                },
            },
            yAxis: {
                title: {
                    text: self.displayMode === 'cost' ? '€' : 'Watt',
                    align: 'high',
                    textAlign: 'right',
                    x: 36,
                    y: -30,
                    rotation: 0,
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontSize: '16px',
                    }
                },
                labels: {
                    align: 'right',
                    x: -8,
                    y: 5,
                    step: 2,
                    formatter() {
                        if (this.value >= 1) {
                            const unit = self.displayMode === 'cost' ? '€' : 'kWh';
                            return `${this.value.toLocaleString('de-DE')} ${unit}`;
                        } else {
                            return null;
                        }
                    },
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '16px'
                    }

                }
            },
            tooltip: {
                useHTML: true,
                formatter() {
                    const idx = this.point.index;
                    const originalData = this.series.userOptions['originalData'].reverse();
                    const year = moment(originalData[idx].timestamp).year();
                    const style = `style="border-color: ${this.color}"`;
                    const name = `<div class="header">${this.key} ${year} </div>`;
                    const unit = self.displayMode === 'cost' ? '€' : 'kWh';
                    let valueFormatted = this.y.toFixed(2);
                    if (self.displayMode === 'cost') {
                        valueFormatted = Math.ceil(this.y).toString();
                    }
                    const value = `<div class="body"> ${valueFormatted} ${unit}</div>`;
                    return `<div class="column-callout" ${style}> ${name} ${value}</div>`;
                },
                borderColor: 'white',
                borderWidth: 0,
                borderRadius: 0,
                backgroundColor: 'transparent',
                shadow: false,
            },
            plotOptions: {
                column: {
                    borderRadius: 3,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                }
            },
            legend: {enabled: false},
            series: [],
            credits: {
                enabled: false
            }
        });
    }

    private initializeDynamicChart() {
        this.seriesLegend = [];
        const self = this;
        this.chart = new Chart({
            chart: {
                type: 'column',
                backgroundColor: 'rgba(255, 255, 255, 0)',
                marginLeft: 80,
                marginBottom: 30,
                marginTop: 50,
                events: {
                    addSeries(evt) {
                        self.seriesLegend.push({name: evt.options.name, color: evt.options.color});
                    },
                    load(load_evt) {
                        if (self.application.isDemoMode()) {
                            self.getMockDynamic();
                            return;
                        }
                        self.getDynamicDiagramData();
                    }
                }
            },
            title: {
                text: null
            },
            xAxis: {
                title: {text: null},
                labels: {
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        color: 'transparent',
                    }
                },
            },
            yAxis: {
                title: {
                    text: self.displayMode === 'cost' ? '€' : 'Watt',
                    align: 'high',
                    textAlign: 'right',
                    x: 36,
                    y: -30,
                    rotation: 0,
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontSize: '16px',
                    }
                },
                labels: {
                    align: 'right',
                    x: -8,
                    y: 5,
                    step: 2,
                    formatter() {
                        if (this.value >= 1) {
                            const unit = self.displayMode === 'cost' ? '€' : 'kWh';
                            return `${this.value.toLocaleString('de-DE')} ${unit}`;
                        } else {
                            return null;
                        }
                    },
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '16px'
                    }

                }
            },
            tooltip: {
                useHTML: true,
                formatter() {
                    const style = `style="border-color: ${this.color}"`;
                    const name = `<div class="header">${this.key}</div>`;
                    const unit = self.displayMode === 'cost' ? '€' : 'kWh';
                    let valueFormatted = this.y.toFixed(2);
                    if (self.displayMode === 'cost') {
                        valueFormatted = Math.ceil(this.y).toString();
                    }
                    const value = `<div class="body"> ${valueFormatted} ${unit}</div>`;
                    return `<div class="column-callout" ${style}> ${name} ${value}</div>`;
                },
                borderColor: 'white',
                borderWidth: 0,
                borderRadius: 0,
                backgroundColor: 'transparent',
                shadow: false,
            },
            plotOptions: {
                column: {
                    borderRadius: 3,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                series: {
                    showInLegend: false
                }
            },
            legend: {enabled: false},
            series: [],
            credits: {
                enabled: false
            }
        });
    }

    private determineStaticDatesByTimeframe(year): { from: Date, to: Date, timeframe: string } {
        let from: Date = null;
        let to: Date = null;
        let timeframe: string = null;

        switch (this.timeframe) {
            case 2: {
                from = new Date();
                from.setFullYear(year.year);
                from.setDate(from.getDate() - (this.position * (this.limit * 7)) + 7);
                from.setDate(from.getDate() - (from.getDay() > 0 ? (from.getDay() - 1) : 6));

                to = new Date();
                to.setFullYear(year.year);
                to.setDate(to.getDate() - (this.position * (this.limit * 7)) + (this.limit * 7));
                to.setDate(to.getDate() + (to.getDay() > 0 ? (7 - to.getDay()) : 0));

                timeframe = 'days';

                break;
            }
            case 3: {
                from = new Date();
                from.setFullYear(year.year);
                from.setMonth(from.getMonth() - (this.position * this.limit) + 1);

                to = new Date();
                to.setFullYear(year.year);
                to.setMonth(to.getMonth() - (this.position * this.limit) + this.limit);

                timeframe = 'months';

                break;
            }
            case 4: {
                from = new Date(0);
                from.setFullYear(year.year);
                from.setMonth(0);

                to = new Date(0);
                to.setFullYear(year.year);
                to.setMonth(11);
                to.setDate(31);

                timeframe = 'months';

                break;
            }
            default: {
                from = new Date();
                from.setFullYear(year.year);
                from.setDate(from.getDate() - (this.position * this.limit) + 1);

                to = new Date();
                to.setFullYear(year.year);
                to.setDate(to.getDate() - (this.position * this.limit) + this.limit);

                timeframe = 'days';
            }
        }

        return {from, to, timeframe};
    }

    private determineDynamicComparisonApiParams(): any {
        let left_from: any = {};
        let left_to: any = {};

        let right_from: any = {};
        let right_to: any = {};

        let api: string = null;

        switch (this.timeframe) {
            case 2: { // Woche
                left_from = new Date(0);
                left_from.setFullYear(this.compare.left_year);
                left_from.setDate(left_from.getDate() + (this.compare.left_kw * 7) - 7);

                left_to = new Date(0);
                left_to.setFullYear(this.compare.left_year);
                left_to.setDate(left_to.getDate() + (this.compare.left_kw * 7) - 1);

                right_from = new Date(0);
                right_from.setFullYear(this.compare.right_year);
                right_from.setDate(right_from.getDate() + (this.compare.right_kw * 7) - 7);

                right_to = new Date(0);
                right_to.setFullYear(this.compare.right_year);
                right_to.setDate(right_to.getDate() + (this.compare.right_kw * 7) - 1);

                api = 'days';

                break;
            }
            case 3: { // Monat
                left_from = new Date(0);
                left_from.setDate(1);
                left_from.setMonth(this.compare.left_month - 1);
                left_from.setFullYear(this.compare.left_year);

                left_to = left_from;

                right_from = new Date(0);
                right_from.setDate(1);
                right_from.setMonth(this.compare.right_month - 1);
                right_from.setFullYear(this.compare.right_year);

                right_to = right_from;

                api = 'months';

                break;
            }
            case 4: { // Jahr
                left_from = new Date(0);
                left_from.setFullYear(this.compare.left_year);

                left_to = new Date(0);
                left_to.setFullYear(this.compare.left_year);
                left_to.setMonth(11);
                left_to.setDate(31);

                right_from = new Date(0);
                right_from.setFullYear(this.compare.right_year);

                right_to = new Date(0);
                right_to.setFullYear(this.compare.right_year);
                right_to.setMonth(11);
                right_to.setDate(31);

                api = 'months';

                break;
            }
            default: { // Tag
                left_from = new Date(0);
                left_from.setFullYear(this.compare.left_year);
                left_from.setMonth(this.compare.left_month - 1);
                left_from.setDate(this.compare.left_day);

                left_to = left_from;

                right_from = new Date(0);
                right_from.setFullYear(this.compare.right_year);
                right_from.setMonth(this.compare.right_month - 1);
                right_from.setDate(this.compare.right_day);

                right_to = right_from;

                api = 'days';
            }
        }

        return {left_from, left_to, right_from, right_to, api};
    }

    private getTariffMultiplier(date): number {
        try {
            date = new Date(date);
            const tariff = this.tariffs.find((el) => {
                const start = moment(el.dateStart)
                    .hours(0)
                    .minutes(0)
                    .seconds(0)
                    .milliseconds(0)
                    .toDate();
                const end = el.dateEnd ? new Date(el.dateEnd) : new Date();
                return date >= start && date <= end;
            });
            return tariff.workPrice;
        } catch (error) {
            return 1;
        }
    }

    private initializeTariffs(): void {
        this.tariffs = this.userService.getTariffInfo();
        of(this.userService.getTariffInfo()).pipe(
            mergeMap(tariffs => tariffs ? of(tariffs) : of(null)),
            mergeMap((tariffs: any) => {
                if (tariffs) {
                    return of(tariffs);
                }
                return this.profile.getContract().pipe(
                    mergeMap(contract => {
                        return this.constructBaseTariff(contract);
                    })
                );
            })
        ).subscribe((result) => {
            this.tariffs = result;
            this.tariffsAvailable = true;
        });
    }

    private constructBaseTariff(response: any): Observable<any> {
        return new Observable<any>((observer) => {
            try {
                const basePrice = typeof response.profile.e_fixed_tariff !== 'undefined' ?
                    parseFloat(response.profile.e_fixed_tariff) :
                    parseFloat(response.profile.budget_bill);
                const workPrice = parseFloat(response.profile.e_tariff);
                const t: Tariff = {
                    name: 'default',
                    dateStart: moment().subtract(3, 'year').toDate(),
                    dateEnd: moment().add(2, 'days').toDate(),
                    basePrice,
                    workPrice
                };
                observer.next([t]);
            } catch (error) {
                observer.error(error);
            }
        });
    }

    /**
     * Determines the value used based on the current data display mode
     * furthermore calculates the price if erna users have entered their own tariff
     * @param consumption
     */
    private determineSeriesValue(consumption): number {
        let value = 0;
        let costMultiplier = 1;
        if (this.displayMode === 'cost') {
            costMultiplier = this.getTariffMultiplier(consumption.timestamp);
            if (this.userService.isEDGUser()) {
                value = 'measured' in consumption ? consumption.measured * costMultiplier : 0;
            } else {
                value = 'cost_measured' in consumption ? consumption.cost_measured * 1000 : 0;
            }
        } else {
            value = 'measured' in consumption ? consumption.measured * costMultiplier : 0;
        }
        return value;
    }


}

enum ComparisonTimeframe {
    DAY = 'day',
    WEEK = 'week',
    MONTH = 'mont',
    YEAR = 'year'
}

enum ComparisonMode {
    STATIC = 'static',
    DYNAMIC = 'dynamic'
}

enum ComparisonDisplayMode {
    COST = 'cost',
    CONSUMPTION = 'consumption'
}
